import React, { useState, useEffect } from 'react';
import './LocationCheckPage.css';

const LocationCheckPage = ({ onContinue }) => {
  const [locationStatus, setLocationStatus] = useState('pending'); // 'pending', 'checking', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Auto-scroll to hide address bar on mobile browsers
    const hideAddressBar = () => {
      setTimeout(() => {
        window.scrollTo(0, 1);
      }, 100);
    };
    hideAddressBar();
    window.addEventListener('orientationchange', hideAddressBar);
    
    // Set up location status listener for SDK callbacks
    const handleLocationUpdate = (data) => {
      console.log('LocationCheckPage received location update:', data);
      if (data.success) {
        setLocationStatus('success');
        // Auto-proceed after successful location check
        setTimeout(() => {
          onContinue();
        }, 1500);
      } else {
        setErrorMessage(data.message || 'Location verification failed');
        setLocationStatus('error');
      }
    };

    // Add listener to window for SDK callbacks
    window.handleLocationCheckResult = handleLocationUpdate;

    return () => {
      // Cleanup
      delete window.handleLocationCheckResult;
      window.removeEventListener('orientationchange', hideAddressBar);
    };
  }, [onContinue]);

  const handleLocationCheck = async () => {
    setLocationStatus('checking');
    
    try {
      // Get hVerifySDK instance
      const sdk = window.hVerifySDKInstance;
      if (!sdk) {
        throw new Error('hVerifySDK not initialized');
      }

      // Use SDK's built-in location method
      const location = await sdk.authorizeLocationCheck();
      
      console.log('Location obtained and sent via hVerifySDK:', location);
      
      // Status will be updated via SDK callbacks
      // The SDK will automatically handle the WebSocket communication
      
    } catch (error) {
      console.error('Location check failed:', error);
      let message = 'Standortzugriff verweigert';
      
      if (error.message.includes('Permission denied')) {
        message = 'Standortzugriff verweigert. Bitte erlauben Sie den Zugriff.';
      } else if (error.message.includes('unavailable')) {
        message = 'Standort nicht verfügbar.';
      } else if (error.message.includes('timeout')) {
        message = 'Standortabfrage Zeitüberschreitung.';
      } else if (error.message.includes('not supported')) {
        message = 'Geolocation wird nicht unterstützt.';
      } else if (error.message.includes('not initialized')) {
        message = 'Verifikationssystem nicht verfügbar.';
      } else {
        message = error.message || 'Unbekannter Fehler';
      }
      
      setErrorMessage(message);
      setLocationStatus('error');
    }
  };

  return (
    <div className="container location-check-container">
      <div>
        <div className="header">
          <div className="logo-placeholder">
            <img src={require('./logo.png')} alt="Logo" />
          </div>
          <div className="headline">Standort verifizieren</div>
          <div className="logo-placeholder" style={{opacity:0}}></div>
        </div>
        <div className="subheadline">Für die Verifikation benötigen wir Ihren aktuellen Standort:</div>
        
        {locationStatus === 'pending' && (
          <div className="location-content">
            <div className="location-icon">
              <svg width="48" height="48" viewBox="0 0 48 48">
                <defs>
                  <linearGradient id="locationGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: '#15335B', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#286ECC', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <circle cx="24" cy="24" r="24" fill="#e8edf2"/>
                <path d="M24 2C16.27 2 10 8.27 10 16c0 10.5 14 28 14 28s14-17.5 14-28c0-7.73-6.27-14-14-14zm0 19c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" fill="url(#locationGradient)"/>
              </svg>
            </div>
            <div className="location-text">
              <div className="location-title">Standort freigeben</div>
              <div className="location-desc">Klicken Sie auf "Standort erlauben" um fortzufahren</div>
            </div>
          </div>
        )}

        {locationStatus === 'checking' && (
          <div className="location-content">
            <div className="location-icon loading">
              <svg width="48" height="48" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="24" fill="#e8edf2"/>
                <path d="M24 6c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2s2-.9 2-2V8c0-1.1-.9-2-2-2zm12.36 6.64c-.78-.78-2.05-.78-2.83 0l-5.66 5.66c-.78.78-.78 2.05 0 2.83.78.78 2.05.78 2.83 0l5.66-5.66c.78-.78.78-2.05 0-2.83zM40 22h-8c-1.1 0-2 .9-2 2s.9 2 2 2h8c1.1 0 2-.9 2-2s-.9-2-2-2zm-6.36 12.36c-.78-.78-2.05-.78-2.83 0-.78.78-.78 2.05 0 2.83l5.66 5.66c.78.78 2.05.78 2.83 0s.78-2.05 0-2.83l-5.66-5.66zM24 40c1.1 0 2-.9 2-2v-8c0-1.1-.9-2-2-2s-2 .9-2 2v8c0 1.1.9 2 2 2zm-12.36-6.64c.78.78 2.05.78 2.83 0 .78-.78.78-2.05 0-2.83l-5.66-5.66c-.78-.78-2.05-.78-2.83 0s-.78 2.05 0 2.83l5.66 5.66zM8 26h8c1.1 0 2-.9 2-2s-.9-2-2-2H8c-1.1 0-2 .9-2 2s.9 2 2 2zm6.36-12.36c.78.78 2.05.78 2.83 0l5.66-5.66c.78-.78.78-2.05 0-2.83s-2.05-.78-2.83 0l-5.66 5.66c-.78.78-.78 2.05 0 2.83z" fill="url(#locationGradient)"/>
              </svg>
            </div>
            <div className="location-text">
              <div className="location-title">Standort wird ermittelt...</div>
              <div className="location-desc">Bitte warten Sie einen Moment</div>
            </div>
          </div>
        )}

        {locationStatus === 'success' && (
          <div className="location-content">
            <div className="location-icon success">
              <svg width="48" height="48" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="24" fill="#4CAF50"/>
                <path d="M20 32.66L10.34 23l2.83-2.83L20 27l14.83-14.83L37.66 15z" fill="#fff"/>
              </svg>
            </div>
            <div className="location-text">
              <div className="location-title">Standort erfolgreich ermittelt</div>
              <div className="location-desc">Weiterleitung zur ID-Überprüfung...</div>
            </div>
          </div>
        )}

        {locationStatus === 'error' && (
          <div className="location-content">
            <div className="location-icon error">
              <svg width="48" height="48" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="24" fill="#f44336"/>
                <path d="M24 14c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2s2-.9 2-2v-8c0-1.1-.9-2-2-2zm0 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="#fff"/>
              </svg>
            </div>
            <div className="location-text">
              <div className="location-title">Standort konnte nicht ermittelt werden</div>
              <div className="location-desc">{errorMessage}</div>
            </div>
          </div>
        )}
      </div>
      
      <div className="bottom-section">
        {locationStatus === 'pending' && (
          <button
            className="weiter-btn active"
            onClick={handleLocationCheck}
          >
            Standort erlauben
          </button>
        )}
        {locationStatus === 'error' && (
          <button
            className="weiter-btn active"
            onClick={handleLocationCheck}
          >
            Erneut versuchen
          </button>
        )}
      </div>
    </div>
  );
};

export default LocationCheckPage;